<template>
    <div class="instrument-details" v-if="instrument">
      <div class="header">
        <h2>
          {{ isEditMode ? "Edit Instrument" : instrument.name }}
          <span v-if="isCardView" class="view-badge">Card</span>
        </h2>

        <div v-if="!isCardView" class="top-buttons">
          <button @click="toggleEditMode" class="icon-btn" title="Edit instrument">
            <i class="bi bi-pencil"></i>
          </button>
          <button @click="toggleCardView" class="icon-btn" title="Show instrument card">
            <i class="bi bi-card-text"></i>
          </button>
          <button @click="downloadInstrumentPdf()" class="icon-btn" title="Download instrument PDF">
            <i class="bi bi-qr-code"></i>
          </button>
          <button class="remove-btn" @click="deleteInstrument" title="Delete instrument">
            <i class="bi bi-trash"></i>
          </button>
        </div>
        <div v-else class="top-buttons">
          <button @click="$refs.cardView.startEdit()" class="icon-btn" :title="hasCard ? 'Edit card' : 'Create card'">
            <i class="bi bi-pencil"></i>
          </button>
          <button @click="toggleCardView" class="icon-btn icon-btn--active" title="Back to instrument details">
            <i class="bi bi-card-text"></i>
          </button>
          <button v-if="hasCard" @click="printCard" class="icon-btn" title="Print card" :disabled="isGeneratingPdf">
            <i class="bi bi-printer"></i>
          </button>
          <button v-if="hasCard" class="remove-btn" @click="$refs.cardView.deleteCard()" title="Delete card">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>

      <InstrumentCard
        v-if="isCardView"
        ref="cardView"
        :instrumentId="instrument.id"
        @hasCardChange="hasCard = $event"
      />

      <!-- Display mode -->
      <div v-else-if="!isEditMode">
        <div class="detail"><strong>ID:</strong> {{ instrument.serial }}</div>
        <div class="detail"><strong>Status:</strong> {{ instrument.status }}</div>
        <div class="detail"><strong>Calibration Time:</strong> {{ formatDate(instrument.c_calibration_time) }}</div>
        <div class="detail"><strong>Next Calibration Time:</strong> {{ formatDate(instrument.n_calibration_time) }}</div>
        <div class="detail"><strong>Maintenance Time:</strong> {{ formatDate(instrument.c_maintenance) }}</div>
        <div class="detail"><strong>Next Maintenance Time:</strong> {{ formatDate(instrument.n_maintenance) }}</div>
        <div class="detail"><strong>Group:</strong> {{ instrument.group || 'N/A' }}</div>
        <div class="detail"><strong>Location:</strong> {{ locationName || 'N/A' }}</div>
        <div class="detail">
          <strong>Support Expenses:</strong>
          <router-link
            v-if="rentalPlaceName"
            :to="{ name: 'RentalPlaceDetailPage', params: { id: instrument.rental_place_id } }"
          >{{ rentalPlaceName }}</router-link>
          <span v-else> N/A</span>
        </div>
        <div class="detail"><strong>Responsible User:</strong> {{ username || 'N/A' }}</div>
        <div v-if="instrument.description" class="detail description">
          <strong>Description:</strong> {{ instrument.description }}
        </div>

      <!-- Metadata Section -->
      <MetadataSection
        v-if="showMetadata"
        :key="instrument.node_id"
        :mdListId="instrument.metadata_list_id"
        :assType="'instrument'"
        :assId="instrument.node_id"
        @mdListCreated="updateMdListId"
        @mdListDetached="clearMdListId"
      />
      </div>
  
      <!-- Edit mode -->
      <div v-else class="edit-form">
        <div class="form-group">
          <label for="name">Name</label>
          <input type="text" id="name" v-model="editableInstrument.name">
        </div>
        <div class="form-group">
          <label for="description">Description</label>
          <textarea id="description" v-model="editableInstrument.description" rows="2"></textarea>
        </div>
        <div class="edit-row">
          <div class="form-group">
            <label for="serial">ID</label>
            <input type="text" id="serial" v-model="editableInstrument.serial">
          </div>
          <div class="form-group">
            <label for="status">Status</label>
            <input type="text" id="status" v-model="editableInstrument.status">
          </div>
        </div>
        <div class="edit-row">
        <div class="form-group">
          <label for="c_calibration_time">Calibration Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="c_calibration_time"
              v-model="editableInstrument.c_calibration_time"
              :disabled="timeNotApplicable.c_calibration_time"
            >
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.c_calibration_time"
                @change="handleNotApplicableToggle('c_calibration_time')"
              >
              <span>N/A</span>
            </label>
          </div>
        </div>
        <div class="form-group">
          <label for="n_calibration_time">Next Calibration Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="n_calibration_time"
              v-model="editableInstrument.n_calibration_time"
              :disabled="timeNotApplicable.n_calibration_time"
            >
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.n_calibration_time"
                @change="handleNotApplicableToggle('n_calibration_time')"
              >
              <span>N/A</span>
            </label>
          </div>
        </div>
        </div>
        <div class="edit-row">
        <div class="form-group">
          <label for="c_maintenance">Maintenance Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="c_maintenance"
              v-model="editableInstrument.c_maintenance"
              :disabled="timeNotApplicable.c_maintenance"
            >
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.c_maintenance"
                @change="handleNotApplicableToggle('c_maintenance')"
              >
              <span>N/A</span>
            </label>
          </div>
        </div>
        <div class="form-group">
          <label for="n_maintenance">Next Maintenance Time</label>
          <div class="datetime-field-row">
            <input
              type="datetime-local"
              id="n_maintenance"
              v-model="editableInstrument.n_maintenance"
              :disabled="timeNotApplicable.n_maintenance"
            >
            <label class="na-toggle na-toggle--inline">
              <input
                type="checkbox"
                v-model="timeNotApplicable.n_maintenance"
                @change="handleNotApplicableToggle('n_maintenance')"
              >
              <span>N/A</span>
            </label>
          </div>
        </div>
        </div>
        <div class="edit-row">
          <div class="form-group">
            <label for="group">Group</label>
            <input type="text" id="group" v-model="editableInstrument.group" list="instrument-group-options">
            <datalist id="instrument-group-options">
              <option v-for="group in groupOptions" :key="group" :value="group"></option>
            </datalist>
          </div>
        <div class="form-group">
          <label for="rental_place_id">Support Expenses</label>
          <select id="rental_place_id" v-model="editableInstrument.rental_place_id">
            <option :value="null">None</option>
            <option v-for="rentalPlace in rentalPlaces" :key="rentalPlace.id" :value="rentalPlace.id">
              {{ rentalPlace.cost_number }}
            </option>
          </select>
        </div>
        </div>
        <div class="buttons-container">
          <button @click="saveChanges">Save</button>
          <button @click="cancelEditMode">Cancel</button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="pdfPreviewUrl" class="pdf-preview-overlay" @click.self="closePdfPreview">
        <div class="pdf-preview-modal">
          <div class="pdf-preview-header">
            <h3>{{ pdfPreviewFilename }}</h3>
            <div v-if="pdfPreviewKind === 'label'" class="pdf-preview-sizes">
              <button
                v-for="size in pdfSizeOptions"
                :key="size.value"
                type="button"
                :class="['pdf-preview-size-btn', { 'pdf-preview-size-btn--active': selectedPdfSize === size.value }]"
                :disabled="isGeneratingPdf"
                @click="setPdfPreviewSize(size.value)"
              >
                {{ size.label }}
              </button>
            </div>
            <div v-else></div>
            <div class="pdf-preview-actions">
              <button type="button" :disabled="isGeneratingPdf" @click="downloadPreviewPdf">Download</button>
              <button type="button" @click="closePdfPreview">Close</button>
            </div>
          </div>
          <div v-if="isGeneratingPdf" class="pdf-preview-loading">
            Generating preview…
          </div>
          <iframe :src="pdfPreviewUrl" :title="pdfPreviewFilename" class="pdf-preview-frame"></iframe>
        </div>
      </div>
    </Teleport>

</template>

<script>
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import NodeMdList from '@/components/NodeMdList.vue';
import InstrumentCard from '@/components/InstrumentCard.vue';
import { buildInstrumentCardPdf } from '@/utils/instrumentCard';
import axios from 'axios';
import { confirmDelete } from "@/utils/confirmDelete";
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { buildDisplayImageUrl, getDisplayImageOrder } from '@/utils/displayImages';

export default {
  components: {
    MetadataSection: NodeMdList,
    InstrumentCard,
  },
  props: {
    instrument: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    showMetadata: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isEditMode: false,
      isCardView: false,
      hasCard: false,
      rentalPlaces: [],
      groupOptions: [],
      pdfPreviewKind: 'label',
      editableInstrument: {},
      timeNotApplicable: {
        c_calibration_time: false,
        n_calibration_time: false,
        c_maintenance: false,
        n_maintenance: false,
      },
      locationName: null,
      username: null,
      pdfPreviewUrl: null,
      pdfPreviewFilename: 'instrument_details.pdf',
      selectedPdfSize: 'medium',
      isGeneratingPdf: false,
      pdfSizeOptions: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
      ],
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState(['loggedInUser']),
    rentalPlaceName() {
      const id = this.instrument.rental_place_id;
      return id ? this.rentalPlaces.find((rentalPlace) => rentalPlace.id === id)?.cost_number || '' : '';
    },
  },
  created() {
    this.fetchLocationName();
    this.fetchUserName(this.instrument.user_id);
    this.fetchRentalPlaces();
  },
  methods: {
    async fetchRentalPlaces() {
      try {
        const response = await axios.get(API_ENDPOINTS.rentalPlaces);
        this.rentalPlaces = response.data;
      } catch (error) {
        console.error('Error fetching rental places:', error);
      }
    },
    async fetchGroupOptions() {
      try {
        const response = await axios.get(API_ENDPOINTS.allInstruments);
        this.groupOptions = [...new Set((response.data || []).map((item) => item.group).filter(Boolean))].sort();
      } catch (error) {
        console.error('Error fetching instrument groups:', error);
      }
    },
    toggleCardView() {
      this.isEditMode = false;
      this.isCardView = !this.isCardView;
      if (!this.isCardView) this.hasCard = false;
    },
    async printCard() {
      const card = this.$refs.cardView?.card;
      if (!card) return;

      this.isGeneratingPdf = true;
      try {
        const blob = await buildInstrumentCardPdf(card, this.getInstrumentUrl());
        this.showPdfPreview(blob, this.getPdfBaseFilename().replace(/_Details\.pdf$/, '_Card.pdf'), 'card');
      } catch (error) {
        console.error('Error generating instrument card PDF:', error);
        this.toast.error('Failed to generate instrument card PDF.');
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    showPdfPreview(blob, filename, kind) {
      const previousPreviewUrl = this.pdfPreviewUrl;
      this.pdfPreviewKind = kind;
      this.pdfPreviewFilename = filename;
      this.pdfPreviewUrl = URL.createObjectURL(blob);
      if (previousPreviewUrl) {
        URL.revokeObjectURL(previousPreviewUrl);
      }
    },
    updateMdListId(newMdListId) {
      const updatedInstrument = { ...this.instrument, metadata_list_id: newMdListId.id };
      this.$emit('updateInstrument', updatedInstrument);
    },
    clearMdListId() {
      const updatedInstrument = { ...this.instrument, metadata_list_id: null };
      this.$emit('updateInstrument', updatedInstrument);
    },
    async fetchLocationName() {
      if (this.instrument.location_id) {
        try {
          const response = await apiService.fetchResources(`${API_ENDPOINTS.locations}/${this.instrument.location_id}`);
          this.locationName = response.name;
        } catch (error) {
          console.error('Error fetching location name:', error);
        }
      }
    },
    async fetchUserName(userId) {
      try {
        const user = await apiService.fetchResources(API_ENDPOINTS.user(userId));
        this.username = user.username; 
      } catch (error) {
        console.error('Error fetching user:', error);
        this.username = 'Unknown user';
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    getInstrumentUrl() {
      return `${window.location.origin}/instruments/${this.instrument.id}`;
    },
    formatFieldLabel(key) {
      const labels = {
        id: 'ID',
        name: 'Name',
        serial: 'ID',
        status: 'Status',
        c_calibration_time: 'Calibration Time',
        n_calibration_time: 'Next Calibration Time',
        c_maintenance: 'Maintenance Time',
        n_maintenance: 'Next Maintenance Time',
        location_id: 'Location ID',
        user_id: 'Responsible User ID',
        metadata_list_id: 'Metadata List ID',
      };

      if (labels[key]) {
        return labels[key];
      }

      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatFieldValue(key, value) {
      if (value === null || value === undefined || value === '') {
        return 'N/A';
      }

      const dateKeys = new Set([
        'c_calibration_time',
        'n_calibration_time',
        'c_maintenance',
        'n_maintenance',
        'created_at',
        'updated_at',
      ]);

      if (dateKeys.has(key)) {
        return this.formatDate(value);
      }

      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : 'N/A';
      }

      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return String(value);
    },
    shouldIncludePdfField(key) {
      if (!key) {
        return false;
      }

      return key !== 'id' && !key.endsWith('_id');
    },
    getPdfFields() {
      const preferredOrder = [
        'name',
        'group',
        'description',
        'serial',
        'status',
        'c_calibration_time',
        'n_calibration_time',
        'c_maintenance',
        'n_maintenance',
      ];
      const fields = [];
      const addedKeys = new Set();

      preferredOrder.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(this.instrument, key)) {
          fields.push({
            label: this.formatFieldLabel(key),
            value: this.formatFieldValue(key, this.instrument[key]),
          });
          addedKeys.add(key);
        }
      });

      fields.push(
        {
          label: 'Location',
          value: this.locationName || 'N/A',
        },
        {
          label: 'Responsible User',
          value: this.username || 'N/A',
        }
      );

      Object.entries(this.instrument).forEach(([key, value]) => {
        if (addedKeys.has(key) || !this.shouldIncludePdfField(key)) {
          return;
        }

        fields.push({
          label: this.formatFieldLabel(key),
          value: this.formatFieldValue(key, value),
        });
      });

      fields.push({
        label: 'Instrument URL',
        value: this.getInstrumentUrl(),
      });

      return fields;
    },
    getPdfFieldsForSize(size) {
      if (size === 'small') {
        return [
          {
            label: 'Instrument Name',
            value: this.instrument?.name || 'N/A',
          },
          {
            label: 'ID',
            value: this.instrument?.serial || 'N/A',
          },
        ];
      }

      return this.getPdfFields();
    },
    getPdfBaseFilename() {
      const safeName = (this.instrument.name || `instrument-${this.instrument.id || 'details'}`)
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '');

      return `${safeName || 'instrument'}_Details.pdf`;
    },
    createPdfDocument(size) {
      const formatMap = {
        small: [90, 120],
        medium: 'a5',
        large: 'a4',
      };

      return new jsPDF({ unit: 'mm', format: formatMap[size] || 'a4' });
    },
    async fetchPrimaryDisplayImageDataUrl() {
      if (!this.instrument?.id || !this.loggedInUser?.entra_id) {
        return null;
      }

      try {
        const response = await apiService.getEntityDisplayImages(
          'instrument',
          this.instrument.id,
          this.loggedInUser.entra_id,
        );
        const images = Array.isArray(response?.display_images)
          ? response.display_images
          : Array.isArray(response)
            ? response
            : [];

        if (!images.length) {
          return null;
        }

        const primaryImage = [...images].sort((left, right) => {
          return getDisplayImageOrder(left, 0) - getDisplayImageOrder(right, 0);
        })[0];

        if (!primaryImage?.file_id) {
          return null;
        }

        const responseBlob = await fetch(
          buildDisplayImageUrl(primaryImage.file_id, this.loggedInUser.entra_id)
        );

        if (!responseBlob.ok) {
          return null;
        }

        const blob = await responseBlob.blob();
        return await this.convertBlobToDataUrl(blob);
      } catch (error) {
        console.error('Failed to fetch primary display image for PDF:', error);
        return null;
      }
    },
    convertBlobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    renderFieldList(pdf, fields, config = {}) {
      const {
        startX = 12,
        startY = 20,
        maxWidth = 80,
        lineHeight = 4.5,
        fieldSpacing = 2.5,
        bottomMargin = 12,
      } = config;

      let y = startY;
      const pageHeight = pdf.internal.pageSize.getHeight();

      fields.forEach(({ label, value }) => {
        const lines = pdf.splitTextToSize(`${label}: ${value}`, maxWidth);
        const blockHeight = lines.length * lineHeight;

        if (y + blockHeight > pageHeight - bottomMargin) {
          pdf.addPage();
          y = startY;
        }

        pdf.text(lines, startX, y);
        y += blockHeight + fieldSpacing;
      });

      return y;
    },
    renderFieldColumns(pdf, fields, config = {}) {
      const {
        startX = 12,
        startY = 20,
        contentWidth = 120,
        gap = 8,
        lineHeight = 4.5,
        fieldSpacing = 2.5,
        bottomMargin = 12,
      } = config;

      const colWidth = (contentWidth - gap) / 2;
      const pageHeight = pdf.internal.pageSize.getHeight();
      let positions = [startY, startY];

      fields.forEach(({ label, value }) => {
        let columnIndex = positions[0] <= positions[1] ? 0 : 1;
        let x = startX + (columnIndex * (colWidth + gap));
        let y = positions[columnIndex];
        const lines = pdf.splitTextToSize(`${label}: ${value}`, colWidth);
        const blockHeight = lines.length * lineHeight;

        if (y + blockHeight > pageHeight - bottomMargin) {
          if (columnIndex === 0 && positions[1] + blockHeight <= pageHeight - bottomMargin) {
            columnIndex = 1;
            x = startX + colWidth + gap;
            y = positions[1];
          } else {
            pdf.addPage();
            positions = [startY, startY];
            columnIndex = 0;
            x = startX;
            y = startY;
          }
        }

        pdf.text(lines, x, y);
        positions[columnIndex] = y + blockHeight + fieldSpacing;
      });

      return Math.max(...positions);
    },
    addContainedImage(pdf, imageDataUrl, box) {
      if (!imageDataUrl) {
        return;
      }

      const { x, y, width, height } = box;
      const imageProperties = pdf.getImageProperties(imageDataUrl);
      const ratio = Math.min(width / imageProperties.width, height / imageProperties.height);
      const renderWidth = imageProperties.width * ratio;
      const renderHeight = imageProperties.height * ratio;
      const offsetX = x + ((width - renderWidth) / 2);
      const offsetY = y + ((height - renderHeight) / 2);

      pdf.addImage(imageDataUrl, imageProperties.fileType || 'JPEG', offsetX, offsetY, renderWidth, renderHeight);
    },
    async buildPdfBlob(size) {
      const instrumentUrl = this.getInstrumentUrl();
      const qrCodeDataUrl = await QRCode.toDataURL(instrumentUrl, {
        width: 480,
        margin: 1,
      });
      const pdf = this.createPdfDocument(size);
      const pageWidth = pdf.internal.pageSize.getWidth();

      if (size === 'small') {
        const margin = 10;
        const qrSize = 52;
        const qrX = (pageWidth - qrSize) / 2;
        const detailWidth = pageWidth - (margin * 2);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        const titleLines = pdf.splitTextToSize(this.instrument.name || 'Instrument', detailWidth);
        pdf.text(titleLines, pageWidth / 2, 16, { align: 'center' });

        pdf.addImage(qrCodeDataUrl, 'PNG', qrX, 24, qrSize, qrSize);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        const serialLines = pdf.splitTextToSize(`ID: ${this.instrument.serial || 'N/A'}`, detailWidth);
        pdf.text(serialLines, pageWidth / 2, 85, { align: 'center' });

        return pdf.output('blob');
      }

      if (size === 'medium') {
        const margin = 10;
        const qrSize = 30;
        const qrX = pageWidth - margin - qrSize;
        const fields = this.getPdfFieldsForSize(size);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        const titleLines = pdf.splitTextToSize(this.instrument.name || 'Instrument', pageWidth - (margin * 3) - qrSize);
        pdf.text(titleLines, margin, 16);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 24);
        pdf.addImage(qrCodeDataUrl, 'PNG', qrX, 10, qrSize, qrSize);

        const urlLines = pdf.splitTextToSize(`URL: ${instrumentUrl}`, pageWidth - (margin * 2));
        pdf.text(urlLines, margin, 33);

        pdf.setFontSize(9.5);
        this.renderFieldColumns(pdf, fields, {
          startX: margin,
          startY: 43,
          contentWidth: pageWidth - (margin * 2),
          gap: 8,
          lineHeight: 4.2,
          fieldSpacing: 2,
          bottomMargin: 10,
        });

        return pdf.output('blob');
      }

      const margin = 14;
      const qrSize = 34;
      const qrX = pageWidth - margin - qrSize;
      const qrY = 14;
      const fields = this.getPdfFieldsForSize(size);
      const displayImageDataUrl = await this.fetchPrimaryDisplayImageDataUrl();

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      const titleLines = pdf.splitTextToSize(this.instrument.name || 'Instrument', pageWidth - (margin * 3) - qrSize);
      pdf.text(titleLines, margin, 20);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 30);
      const urlLines = pdf.splitTextToSize(`URL: ${instrumentUrl}`, pageWidth - (margin * 2));
      pdf.text(urlLines, margin, 38);

      pdf.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      pdf.setFontSize(9);
      pdf.text('Scan to open instrument', qrX - 4, qrY + qrSize + 5);

      if (displayImageDataUrl) {
        const imageBox = {
          x: pageWidth - margin - 76,
          y: 54,
          width: 76,
          height: 64,
        };

        pdf.setDrawColor(220, 226, 232);
        pdf.roundedRect(imageBox.x, imageBox.y, imageBox.width, imageBox.height, 3, 3);
        this.addContainedImage(pdf, displayImageDataUrl, imageBox);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Main Display Picture', imageBox.x, imageBox.y - 4);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        this.renderFieldColumns(pdf, fields, {
          startX: margin,
          startY: 54,
          contentWidth: pageWidth - (margin * 3) - imageBox.width,
          gap: 8,
          lineHeight: 4.6,
          fieldSpacing: 2.4,
          bottomMargin: 14,
        });
      } else {
        pdf.setFontSize(10);
        this.renderFieldColumns(pdf, fields, {
          startX: margin,
          startY: 54,
          contentWidth: pageWidth - (margin * 2),
          gap: 10,
          lineHeight: 4.6,
          fieldSpacing: 2.4,
          bottomMargin: 14,
        });
      }

      return pdf.output('blob');
    },
    async downloadInstrumentPdf(size = this.selectedPdfSize) {
      this.selectedPdfSize = size;
      this.isGeneratingPdf = true;

      try {
        const pdfBlob = await this.buildPdfBlob(size);
        this.showPdfPreview(pdfBlob, this.getPdfBaseFilename(), 'label');
        this.toast.success('Instrument PDF preview opened.');
      } catch (error) {
        console.error('Error generating instrument PDF:', error);
        this.toast.error('Failed to generate instrument PDF.');
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    async setPdfPreviewSize(size) {
      if (this.isGeneratingPdf || this.selectedPdfSize === size && this.pdfPreviewUrl) {
        this.selectedPdfSize = size;
        return;
      }

      await this.downloadInstrumentPdf(size);
    },
    downloadPreviewPdf() {
      if (!this.pdfPreviewUrl) {
        return;
      }

      const link = document.createElement('a');
      link.href = this.pdfPreviewUrl;
      link.download = this.pdfPreviewFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    closePdfPreview() {
      if (this.pdfPreviewUrl) {
        URL.revokeObjectURL(this.pdfPreviewUrl);
        this.pdfPreviewUrl = null;
      }
    },
    initializeTimeNotApplicableState(source = {}) {
      this.timeNotApplicable = {
        c_calibration_time: !source?.c_calibration_time,
        n_calibration_time: !source?.n_calibration_time,
        c_maintenance: !source?.c_maintenance,
        n_maintenance: !source?.n_maintenance,
      };
    },
    handleNotApplicableToggle(field) {
      if (this.timeNotApplicable[field]) {
        this.editableInstrument = {
          ...this.editableInstrument,
          [field]: null,
        };
        return;
      }

      this.editableInstrument = {
        ...this.editableInstrument,
        [field]: this.editableInstrument[field] || '',
      };
    },
    toggleEditMode() {
      this.isEditMode = true;
      this.editableInstrument = JSON.parse(JSON.stringify(this.instrument));
      this.initializeTimeNotApplicableState(this.instrument);
      if (!this.groupOptions.length) this.fetchGroupOptions();
    },
    async saveChanges() {
      const updatedInstrument = {
        ...this.editableInstrument,
        description: this.editableInstrument.description?.trim() || null,
        group: this.editableInstrument.group?.trim() || null,
        c_calibration_time: this.timeNotApplicable.c_calibration_time ? null : (this.editableInstrument.c_calibration_time || null),
        n_calibration_time: this.timeNotApplicable.n_calibration_time ? null : (this.editableInstrument.n_calibration_time || null),
        c_maintenance: this.timeNotApplicable.c_maintenance ? null : (this.editableInstrument.c_maintenance || null),
        n_maintenance: this.timeNotApplicable.n_maintenance ? null : (this.editableInstrument.n_maintenance || null),
      };

      this.$emit('updateInstrument', updatedInstrument);
      this.isEditMode = false;
    },
    cancelEditMode() {
      this.isEditMode = false;
      this.initializeTimeNotApplicableState(this.instrument);
    },
    async deleteInstrument() {
      const instrumentId = this.instrument.id;
      try {
        const isConfirmed = await confirmDelete("Are you sure you want to delete this Instrument?");
        if (!isConfirmed) return; 
        
        await apiService.deleteResource(API_ENDPOINTS.instrument(instrumentId), this.loggedInUser.id, this.loggedInUser.entra_id);
        this.$router.push({ name: "Instruments" });
      } catch (error) {
        console.error('Error deleting instrument:', error);
      }
    }
  },
  beforeUnmount() {
    this.closePdfPreview();
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.instrument-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: flex-end;
  }
  .top-buttons {
      display: flex;
      gap: 10px;
    }
  .icon-btn--active {
    background-color: $primary-color;
    color: #fff;
    border-radius: 6px;
  }
  .view-badge {
    margin-left: 8px;
    padding: 2px 10px;
    border-radius: 999px;
    background: rgba($primary-color, 0.1);
    font-size: 0.85rem;
    font-weight: 600;
    vertical-align: middle;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      color: $primary-color;
      margin-bottom: 20px;
    }
  }
  .detail {
    margin-bottom: 3px;
    font-size: 16px;
    strong {
      color: black;
    }
  }

  .description {
    margin: 0 0 10px;
    white-space: pre-wrap;
  }

  .edit-form {
    .form-group {
      margin-bottom: 8px;
      min-width: 0;

      > label {
        margin-bottom: 2px;
        font-size: 0.85rem;
        font-weight: 600;
      }
    }

    input[type="text"],
    input[type="datetime-local"],
    textarea,
    select {
      margin-top: 0;
      padding: 4px 8px;
    }

    textarea {
      resize: vertical;
    }
  }

  .edit-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 12px;
  }

  .datetime-field-row {
    display: flex;
    align-items: center;
    gap: 12px;

    input[type="datetime-local"] {
      flex: 1;
      min-width: 0;
    }
  }

  .na-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .na-toggle--inline {
    flex-shrink: 0;
  }
}

.pdf-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
}

.pdf-preview-modal {
  width: min(1200px, calc(100vw - 48px));
  height: calc(100vh - 48px);
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}

.pdf-preview-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;

  h3 {
    margin: 0;
    color: $primary-color;
    font-size: 1rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.pdf-preview-sizes {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pdf-preview-size-btn {
  border: 1px solid rgba($primary-color, 0.18);
  border-radius: 999px;
  padding: 8px 14px;
  background: rgba($primary-color, 0.06);
  color: $primary-color;
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.pdf-preview-size-btn--active {
  background: $primary-color;
  color: #fff;
}

.pdf-preview-actions {
  display: flex;
  gap: 10px;

  button {
    border: none;
    border-radius: 8px;
    padding: 8px 12px;
    background: $primary-color;
    color: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}

.pdf-preview-loading {
  padding: 12px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba($primary-color, 0.06);
  color: rgba($primary-color, 0.9);
  font-weight: 600;
}

.pdf-preview-frame {
  width: 100%;
  flex: 1;
  border: 0;
  background: #f5f5f5;
}

@media (max-width: 900px) {
  .pdf-preview-header {
    grid-template-columns: 1fr;
  }

  .pdf-preview-sizes,
  .pdf-preview-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
